import React, { useState } from 'react';
import { Alert } from '../../../../../CommonComponents/OPL/Alert/Alert';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../CommonComponents/OPL/Button/Button';
import { Icon } from '../../../../../CommonComponents/OPL/Icon/Icon';
import { Input } from '../../../../../CommonComponents/OPL/Input/Input';
import { Loading } from '../../../../../CommonComponents/OPL/Loading/Loading';
import { ModalBody } from '../../../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalFooter } from '../../../../../CommonComponents/OPL/Modal/ModalFooter';
import { Radio } from '../../../../../CommonComponents/OPL/Radio/Radio';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { Tooltip } from '../../../../../CommonComponents/OPL/Tooltip/Tooltip';
import { useCreateFolderMutation } from '../../../../api/useCreateFolderMutation';
import { useMoveObjectsMutation } from '../../../../api/useMoveObjectsMutation';
import { EntityTypeDTO, MAX_SUB_FOLDER_COUNT, ProviderType } from '../../../../DocumentVault.meta';
import { pathList } from '../../../../hooks/useBreadcrumbsItems';
import { useRouteParams } from '../../../../hooks/useRouteParams';
import { useFilterableYearOption } from '../../../../state/selectors';
import { ENTITY_ICONS } from '../../../Entity/Entity.meta';
import { useOrganizeItemsContext } from '../../context/useOrganizeItemsContext';

const NEW_FOLDER = 'New Folder Name';

export const MoveItems = () => {
  const {
    data: currentFolderEntity,
    sourcePath,
    destinationPath,
    setDestinationPath,
    onNext,
    onBack,
    setNavigatePath,
    navigatePath,
    sourceEntities,
    isLoading: isFetchingEntities,
  } = useOrganizeItemsContext();

  const { clientId, path, providerType, filterableByYear } = useRouteParams();

  const { value: year } = useFilterableYearOption();

  const folderPath = decodeURIComponent(navigatePath ?? path);
  const [folderName, setFolderName] = useState<string | null>(null);

  const {
    mutateAsync: createFolderMutation,
    isLoading: isCreatingFolder,
    error: createFolderError,
  } = useCreateFolderMutation();

  const {
    mutateAsync: moveObject,
    isLoading: isMovingObject,
    error: moveObjectError,
  } = useMoveObjectsMutation();

  const folderClash = destinationPath
    ? sourcePath.some(source => destinationPath.startsWith(source))
    : false;

  const isLoading = isCreatingFolder || isMovingObject;
  const error = createFolderError || moveObjectError;

  if (isFetchingEntities) {
    return (
      <ModalBody>
        <Box display="flex" flexDirection="column" mt="24" minHeight={400}>
          <Text fontWeight={600}>
            Select the destination for the {sourcePath.length} item
            {sourcePath.length === 1 ? ' ' : 's '}
            you want to move:
          </Text>

          <Box
            alignItems="center"
            display="flex"
            flex={1}
            height="100%"
            justifyContent="center"
            width="100%"
          >
            <Loading />
          </Box>
        </Box>
      </ModalBody>
    );
  }

  if (!currentFolderEntity) {
    return null;
  }

  const currentFolderPath = currentFolderEntity.path || '';
  const sourceEntityPath = sourceEntities.path;

  const treeList = pathList(currentFolderPath);

  const newFolderLimit = treeList.length <= MAX_SUB_FOLDER_COUNT;

  return (
    <>
      <ModalBody>
        <Text fontWeight={600}>
          Select the destination for the {sourcePath.length} item
          {sourcePath.length === 1 ? ' ' : 's '}
          you want to move:
        </Text>

        {(folderClash ||
          (destinationPath === NEW_FOLDER && folderName === null) ||
          (destinationPath === NEW_FOLDER && folderName === '')) && (
          <Alert variant="info" mt="8">
            {folderClash
              ? `Cannot move or create a folder into itself or its subfolder`
              : 'Please enter a folder name '}
          </Alert>
        )}

        {error && (
          <Alert variant="error" mt="8">
            {error?.message}
          </Alert>
        )}

        <Box
          display="flex"
          flexDirection="column"
          css={({ theme }) => ({ gap: theme.space[16] })}
          mt="24"
        >
          {treeList.map((treeItem, index) => {
            if (treeItem.path !== currentFolderPath) {
              return (
                <Box
                  key={treeItem.id}
                  display="flex"
                  alignItems="center"
                  style={{ marginLeft: `${index * 16}px` }}
                >
                  <Button
                    variant="link"
                    leftIcon="far fa-folder-open"
                    variantColor="primary"
                    onClick={() => {
                      setDestinationPath(treeItem.path);

                      setNavigatePath(treeItem.path);
                    }}
                    size="large"
                  >
                    {treeItem.title}
                  </Button>
                </Box>
              );
            }

            const canCreateNewFolder = !sourcePath.some(source =>
              (currentFolderEntity?.path || '').startsWith(source),
            );

            return (
              <Box
                pl="2"
                display="flex"
                flexDirection="column"
                key={treeItem.id}
                css={({ theme }) => ({
                  gap: `${theme.space[8]}px`,
                })}
                style={{ marginLeft: `${index * 16}px` }}
              >
                <Box display="flex" alignItems="center">
                  <Radio
                    disabled={
                      folderClash ||
                      currentFolderPath === sourceEntityPath ||
                      currentFolderEntity.isReadOnly
                    }
                    checked={destinationPath === treeItem.path}
                    onChange={() => {
                      if (!folderClash && currentFolderPath !== sourceEntityPath) {
                        setDestinationPath(treeItem.path);
                      }
                    }}
                  >
                    <Icon
                      fontSize="h5"
                      className={
                        currentFolderEntity.isReadOnly
                          ? 'far fa-folder-times'
                          : 'far fa-folder-open'
                      }
                      color="neutral.500"
                    />
                  </Radio>

                  <Text fontWeight={600} ml="8">
                    {treeItem.title}
                  </Text>

                  {(folderClash ||
                    currentFolderPath === sourceEntityPath ||
                    currentFolderEntity.isReadOnly) && (
                    <Tooltip
                      content={
                        currentFolderEntity.isReadOnly
                          ? 'Folder is read-only'
                          : 'Source of the items being moved'
                      }
                      placement="top"
                    >
                      <Icon className="far fa-info-circle" color="neutral.500" ml="4" />
                    </Tooltip>
                  )}
                </Box>

                <Box
                  ml="16"
                  display="flex"
                  flexDirection="column"
                  css={({ theme }) => ({ gap: `${theme.space[12]}px` })}
                >
                  {currentFolderEntity.items.map(entity => {
                    const isSourcePath = sourcePath.some(source => entity.path.startsWith(source));

                    if (entity.type !== EntityTypeDTO.Folder) {
                      return null;
                    }

                    return (
                      <Box key={entity.name} display="flex" alignItems="center">
                        <Box display="flex" alignItems="center">
                          <Radio
                            disabled={
                              entity.areItemsInsideReadOnly ||
                              sourceEntityPath === entity.path ||
                              isSourcePath
                            }
                            checked={destinationPath === entity.path}
                            onChange={() => {
                              setNavigatePath(entity.path);

                              if (sourceEntityPath !== entity.path && !isSourcePath) {
                                setDestinationPath(entity.path);
                              }
                            }}
                          >
                            <Icon
                              fontSize="h6"
                              className={
                                entity.areItemsInsideReadOnly
                                  ? 'far fa-folder-times'
                                  : sourceEntityPath === entity.path
                                  ? 'far fa-folder-open'
                                  : ENTITY_ICONS[entity.type]
                              }
                              color="neutral.500"
                              mr="4"
                            />
                          </Radio>

                          <Button
                            disabled={isSourcePath || entity.areItemsInsideReadOnly}
                            variant="link"
                            variantColor="primary"
                            onClick={() => {
                              setNavigatePath(entity.path);
                              if (sourceEntityPath !== entity.path && !isSourcePath) {
                                setDestinationPath(entity.path);
                              }
                            }}
                            size="large"
                          >
                            {entity.name}
                          </Button>

                          {(entity.areItemsInsideReadOnly ||
                            isSourcePath ||
                            sourceEntityPath === entity.path) && (
                            <Tooltip
                              placement="top"
                              content={
                                entity.areItemsInsideReadOnly
                                  ? 'Folder is read-only'
                                  : sourceEntityPath === entity.path
                                  ? 'Source of the items being moved 2 '
                                  : 'Cannot move to the same folder'
                              }
                            >
                              <Icon className="far fa-info-circle" color="neutral.500" ml="4" />
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>

                {canCreateNewFolder && !currentFolderEntity.isReadOnly && newFolderLimit && (
                  <Box key={NEW_FOLDER} display="flex" alignItems="center" ml="16">
                    <Radio
                      mb="4"
                      checked={destinationPath === NEW_FOLDER}
                      onChange={() => {
                        setDestinationPath(NEW_FOLDER);
                      }}
                      helperText="Create a new folder and move items into it"
                    >
                      <Box display="flex" alignItems="center">
                        <Icon
                          fontSize="h5"
                          className={ENTITY_ICONS.Folder}
                          color="neutral.500"
                          mr="8"
                        />

                        <Input
                          sizeVariant="small"
                          value={folderName || ''}
                          placeholder={NEW_FOLDER}
                          onChange={({ target: { value } }) => {
                            setDestinationPath(NEW_FOLDER);
                            setFolderName(value);
                          }}
                        />
                      </Box>
                    </Radio>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </ModalBody>

      <ModalFooter>
        <Button variant="subtleInactive" mr="8" onClick={onBack} disabled={isLoading}>
          Back
        </Button>

        <Button
          isLoading={isLoading}
          disabled={
            folderClash ||
            (destinationPath === NEW_FOLDER && folderName === null) ||
            (destinationPath === NEW_FOLDER && folderName === '') ||
            destinationPath === null ||
            isLoading ||
            isFetchingEntities ||
            currentFolderEntity.isReadOnly
          }
          onClick={async () => {
            try {
              if (destinationPath === null) {
                throw new Error('Please select a destination folder.');
              }

              if (destinationPath === NEW_FOLDER) {
                const finalFolderPath = `${folderPath}${folderName}/`;

                if (providerType === ProviderType.CloudStorage) {
                  await moveObject({ destinationPath: finalFolderPath, sourcePath, clientId });
                } else {
                  const currentYear = filterableByYear ? year : null;

                  const newFolderDetails = await createFolderMutation({
                    clientId,
                    folderData: {
                      folderPath: finalFolderPath,
                      path,
                      providerType,
                      currentYear,
                    },
                  });

                  await moveObject({
                    destinationPath: newFolderDetails.path,
                    sourcePath,
                    clientId,
                  });
                }
              } else {
                await moveObject({ destinationPath, sourcePath, clientId });
              }

              onNext();
            } catch (e) {
              console.error('Error moving items:', e);
            }
          }}
        >
          Confirm Move
        </Button>
      </ModalFooter>
    </>
  );
};
